import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { supabase } from '@/lib/supabase';

export function OAuthCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState('Connecting your account...');

  useEffect(() => {
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const error = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');

    if (error) {
      setStatus('error');
      setMessage(errorDescription || error);
      return;
    }

    if (!code || !state) {
      setStatus('error');
      setMessage('Missing authorization code or provider state.');
      return;
    }

    const provider = state as 'microsoft' | 'google';
    const redirectUri = `${window.location.origin}/oauth/callback`;

    supabase.functions
      .invoke('oauth-callback', {
        body: { provider, code, redirect_uri: redirectUri },
      })
      .then(({ data, error: fnError }) => {
        if (fnError || !data?.success) {
          setStatus('error');
          setMessage(fnError?.message || data?.error || 'Failed to connect account.');
          return;
        }
        setStatus('success');
        setMessage(`Connected ${data.connected_email || provider} successfully!`);
        setTimeout(() => navigate('/email', { replace: true }), 2000);
      })
      .catch((err) => {
        setStatus('error');
        setMessage(String(err));
      });
  }, [searchParams, navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="text-center space-y-4">
        {status === 'processing' && (
          <Loader2 className="mx-auto h-10 w-10 animate-spin text-primary" />
        )}
        {status === 'success' && (
          <CheckCircle className="mx-auto h-10 w-10 text-emerald-500" />
        )}
        {status === 'error' && (
          <XCircle className="mx-auto h-10 w-10 text-destructive" />
        )}
        <p className="text-sm text-muted-foreground max-w-sm">{message}</p>
        {status === 'error' && (
          <button
            onClick={() => navigate('/email', { replace: true })}
            className="text-sm text-primary hover:underline"
          >
            Back to Email
          </button>
        )}
      </div>
    </div>
  );
}
